import axios from 'axios';

const YOUTUBE_API_BASE_URL = 'https://www.googleapis.com/youtube/v3';

/**
 * Fetches comments from a YouTube video
 * @param {string} videoId - YouTube video ID
 * @param {string} apiKey - YouTube Data API key
 * @param {number} maxResults - Maximum number of comments to fetch (default: 100)
 * @param {Date|null} sinceDate - Optional: Only fetch comments published/updated after this date
 * @returns {Promise<Array>} - Array of comment objects
 */
export const fetchVideoComments = async (videoId, apiKey, maxResults = 10000, sinceDate = null) => {
  if (!videoId || !apiKey) {
    throw new Error('Video ID and API key are required');
  }

  try {
    // First, get the video details to ensure it exists
    const videoResponse = await axios.get(`${YOUTUBE_API_BASE_URL}/videos`, {
      params: {
        part: 'snippet,statistics,contentDetails',
        id: videoId,
        key: apiKey,
      },
    });

    if (!videoResponse.data.items || videoResponse.data.items.length === 0) {
      throw new Error('Video not found or not accessible');
    }

    // Extract video metadata
    const videoData = videoResponse.data.items[0];
    
    // Parse duration (ISO 8601 format: PT4M13S = 4 minutes 13 seconds)
    const parseDuration = (duration) => {
      if (!duration) return 'Unknown';
      const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
      if (!match) return duration;
      const hours = parseInt(match[1] || 0);
      const minutes = parseInt(match[2] || 0);
      const seconds = parseInt(match[3] || 0);
      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      }
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };
    
    const videoMetadata = {
      videoId: videoId,
      videoTitle: videoData.snippet?.title || '',
      channelName: videoData.snippet?.channelTitle || '',
      thumbnailUrl: videoData.snippet?.thumbnails?.high?.url || videoData.snippet?.thumbnails?.default?.url || '',
      description: videoData.snippet?.description || '',
      publishedAt: videoData.snippet?.publishedAt || '',
      viewCount: parseInt(videoData.statistics?.viewCount || 0),
      likeCount: parseInt(videoData.statistics?.likeCount || 0),
      commentCount: parseInt(videoData.statistics?.commentCount || 0), // Total comments on the video
      duration: parseDuration(videoData.contentDetails?.duration),
    };

    // Fetch comment threads (top-level comments)
    const comments = [];
    let nextPageToken = null;
    let fetchedCount = 0;
    const sinceTimestamp = sinceDate ? new Date(sinceDate).getTime() : null;

    do {
      const commentsResponse = await axios.get(
        `${YOUTUBE_API_BASE_URL}/commentThreads`,
        {
          params: {
            part: 'snippet,replies',
            videoId: videoId,
            key: apiKey,
            maxResults: Math.min(100, maxResults - fetchedCount), // YouTube API max is 100 per request
            pageToken: nextPageToken,
            order: sinceDate ? 'time' : 'relevance', // Use 'time' when fetching new comments, 'relevance' for full analysis
          },
        }
      );

      const items = commentsResponse.data.items || [];
      
      for (const item of items) {
        const topLevelComment = item.snippet.topLevelComment.snippet;
        const topLevelCommentId = item.snippet.topLevelComment.id;
        
        // Filter by date if sinceDate is provided
        const commentPublishedAt = new Date(topLevelComment.publishedAt).getTime();
        const commentUpdatedAt = new Date(topLevelComment.updatedAt).getTime();
        
        // Include comment if it's new (published or updated after sinceDate)
        if (sinceTimestamp) {
          if (commentPublishedAt <= sinceTimestamp && commentUpdatedAt <= sinceTimestamp) {
            // This comment is older than our threshold, but we might still want to check replies
            // For now, skip old comments when fetching new ones
            continue;
          }
        }
        
        comments.push({
          id: item.id, // Thread ID
          commentId: topLevelCommentId, // Actual comment ID for direct linking
          videoId: videoId, // Store video ID for generating reply URLs
          author: topLevelComment.authorDisplayName,
          authorChannelId: topLevelComment.authorChannelId?.value,
          text: topLevelComment.textDisplay,
          likeCount: topLevelComment.likeCount,
          publishedAt: topLevelComment.publishedAt,
          updatedAt: topLevelComment.updatedAt,
          isOwner: topLevelComment.isVideoOwner || false,
          replies: item.replies?.comments?.map(reply => ({
            id: reply.id,
            author: reply.snippet.authorDisplayName,
            text: reply.snippet.textDisplay,
            likeCount: reply.snippet.likeCount,
            publishedAt: reply.snippet.publishedAt,
          })) || [],
        });

        fetchedCount++;
        if (fetchedCount >= maxResults) break;
      }

      nextPageToken = commentsResponse.data.nextPageToken;
    } while (nextPageToken && fetchedCount < maxResults);

    // Return comments with video metadata
    return {
      comments,
      videoMetadata,
    };
  } catch (error) {
    if (error.response) {
      // API error response
      const status = error.response.status;
      const message = error.response.data?.error?.message || error.message;
      
      if (status === 403) {
        throw new Error('API key is invalid or quota exceeded. Please check your YouTube Data API key.');
      } else if (status === 404) {
        throw new Error('Video not found or comments are disabled.');
      } else {
        throw new Error(`YouTube API error: ${message}`);
      }
    }
    throw error;
  }
};
